import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

interface WebChatContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  /**
   * Element that had focus at the moment the chat was opened, captured
   * synchronously inside `setIsOpen(true)` BEFORE React schedules the
   * unmount of the launcher. Read by `LazyWebChat` on close to restore
   * focus. Cleared after restoration.
   */
  openerRef: React.MutableRefObject<HTMLElement | null>;
}

const WebChatContext = createContext<WebChatContextType | undefined>(undefined);

/** sessionStorage key that mirrors the chat panel's open/closed state across
 *  reloads within the same browser tab. Read once on mount, written on every
 *  isOpen change. Not synced across tabs (sessionStorage is per-tab) and does
 *  not persist conversation contents — only the panel visibility.
 *
 *  Exported so tests can seed the storage state without duplicating the key
 *  literal — if the key ever changes, the test breaks at compile time. */
export const CHAT_OPEN_STORAGE_KEY = "chatOpen";

export const WebChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpenState] = useState(false);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const saved = sessionStorage.getItem(CHAT_OPEN_STORAGE_KEY);
    if (saved === "true") {
      setIsOpenState(true);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(CHAT_OPEN_STORAGE_KEY, isOpen.toString());
  }, [isOpen]);

  // Wraps the raw setter so we can capture the focused element BEFORE the
  // state flip triggers a re-render that unmounts the launcher button. By
  // the time React's effect phase runs, `document.activeElement` has
  // already fallen back to <body> — too late to remember the opener.
  const setIsOpen = useCallback((open: boolean) => {
    if (open && !openerRef.current) {
      openerRef.current = document.activeElement as HTMLElement | null;
    }
    setIsOpenState(open);
  }, []);

  return (
    <WebChatContext.Provider value={{ isOpen, setIsOpen, openerRef }}>
      {children}
    </WebChatContext.Provider>
  );
};

export const useWebChatContext = (): WebChatContextType => {
  const context = useContext(WebChatContext);
  if (!context) {
    throw new Error("useWebChatContext must be used within a WebChatProvider");
  }
  return context;
};
